import React, { useState, useEffect } from "react";
import BottomNav from "../components/BottomNav";
import {
  Settings,
  User as UserIcon,
  Bell,
  HelpCircle,
  Info,
  ChevronRight,
  LogOut,
  Sparkles,
  Mail,
  Check,
} from "lucide-react";

const MENU = [
  { key: "personal", label: "Şəxsi məlumatlarım", Icon: UserIcon, tint: "cyan" },
  { key: "notifications", label: "Bildirişlər", Icon: Bell, tint: "orange", dot: true },
  { key: "faq", label: "Tez-tez verilən suallar", Icon: HelpCircle, tint: "purple" },
  { key: "about", label: "Haqqımızda", Icon: Info, tint: "blue" },
];

const PREFS_KEY = "pulpay_notif_prefs_v1";
const EMAIL_KEY = "pulpay_user_email_v1";
const DEFAULT_PREFS = { push: true, sms: true, email: false, marketing: true };

function readPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch (e) { return DEFAULT_PREFS; }
}

function buildContent(user, userPhone) {
  return {
    personal: {
      title: "Şəxsi məlumatlarım",
      rows: [
        { label: "Ad Soyad", value: user ? `${user.firstName} ${user.lastName}` : "—" },
        { label: "Mobil nömrə", value: userPhone || "—" },
        { label: "FIN", value: user?.fin || "—" },
        { label: "Vəsiqə seriya", value: user?.docSeries || "—" },
        { label: "Vəsiqə nömrəsi", value: user?.docNumber || "—" },
        { label: "Doğum tarixi", value: user?.birthDate || "—" },
      ],
    },
    faq: {
      title: "Tez-tez verilən suallar",
      rows: [
        { label: "Kredit neçə dəqiqədə təsdiqlənir?", value: "15 dəqiqə" },
        { label: "Minimum sənəd", value: "Vəsiqə + FIN" },
        { label: "Rəsmi gəlir tələb olunur?", value: "Xeyr" },
        { label: "Kredit müddəti", value: "1 – 36 ay" },
        { label: "İllik faiz", value: "10%-dən" },
      ],
    },
    about: {
      title: "Haqqımızda",
      rows: [
        { label: "Şirkət", value: "PULPAY MMC" },
        { label: "Versiya", value: "1.0.0" },
        { label: "Ünvan", value: "Bakı, Azərbaycan" },
        { label: "Dəstək", value: "info@pulpay.az" },
        { label: "Telefon", value: "+994 12 123 45 67" },
        { label: "Lisenziya №", value: "AZ-KRD-2024-1187" },
        { label: "Sertifikat №", value: "CBA-FSC-0043721" },
        { label: "VÖEN", value: "1804258951" },
      ],
      note: "PULPAY Azərbaycan Respublikasının Mərkəzi Bankı tərəfindən təsdiqlənmiş və nəzarət altında fəaliyyət göstərən lisenziyalı kredit təşkilatıdır.",
    },
  };
}

const NOTIF_OPTIONS = [
  { key: "push", label: "Push bildirişlər", desc: "Anlıq mobil bildirişlər" },
  { key: "sms", label: "SMS bildirişlər", desc: "Nömrənizə SMS mesajı" },
  { key: "marketing", label: "Marketinq təklifləri", desc: "Xüsusi kampaniya və endirimlər" },
];

function NotificationsPanel({ onClose, notify, history = [], onClearHistory }) {
  const [prefs, setPrefs] = useState(readPrefs);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch (e) {}
  }, [prefs]);

  const toggle = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <div className="pf-notif-panel" data-testid="notifications-panel">
      <div className="pf-notif-list">
        {NOTIF_OPTIONS.map((opt) => {
          const on = !!prefs[opt.key];
          return (
            <div
              key={opt.key}
              className="pf-notif-row"
              data-testid={`notif-row-${opt.key}`}
            >
              <div className="pf-notif-txt">
                <b>{opt.label}</b>
                <span>{opt.desc}</span>
              </div>
              <button
                type="button"
                className={`pf-toggle ${on ? "on" : ""}`}
                onClick={() => toggle(opt.key)}
                aria-pressed={on}
                aria-label={opt.label}
                data-testid={`notif-toggle-${opt.key}`}
              >
                <span className="pf-toggle-knob" />
              </button>
            </div>
          );
        })}
      </div>

      <button className="pf-modal-ok" onClick={onClose} data-testid="notif-close-ok">
        Yadda saxla
      </button>
    </div>
  );
}

function ProfileInfoModal({ item, activeKey, onClose, notify, history, onClearHistory }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const isNotif = activeKey === "notifications";
  const title = isNotif ? "Bildirişlər" : (item && item.title);

  return (
    <div className="pf-modal-overlay" onClick={onClose} data-testid="profile-modal-overlay">
      <div className="pf-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" data-testid="profile-modal">
        <button className="pf-modal-close" onClick={onClose} aria-label="Bağla" data-testid="profile-modal-close">×</button>
        <div className="pf-modal-head">
          <div className="pf-modal-logo">
            <img src="/assets/logo.png" alt="" />
          </div>
          <h3 data-testid="profile-modal-title">{title}</h3>
        </div>
        {isNotif ? (
          <NotificationsPanel
            onClose={onClose}
            notify={notify}
            history={history}
            onClearHistory={onClearHistory}
          />
        ) : (
          <>
            <div className="pf-modal-rows">
              {item.rows.map((r, i) => (
                <div className="pf-modal-row" key={i}>
                  <span>{r.label}</span>
                  <b>{r.value}</b>
                </div>
              ))}
            </div>
            {item.note && <p className="pf-modal-note">{item.note}</p>}
            <button className="pf-modal-ok" onClick={onClose} data-testid="profile-modal-ok">Anladım</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Profile({ go, notify, userPhone, user, onLogout, notifsHistory = [], onClearNotifs, onMarkNotifsRead }) {
  const [active, setActive] = useState(null);
  const firstName = user?.firstName || "Qonaq";
  const gender = user?.gender || null;
  const genderEmoji = gender === "female" ? "👩‍🦰" : gender === "male" ? "👨" : null;
  const content = buildContent(user, userPhone);
  const unreadCount = notifsHistory.filter((n) => n.unread).length;

  useEffect(() => {
    if (active === "notifications" && unreadCount > 0 && onMarkNotifsRead) {
      const t = setTimeout(() => onMarkNotifsRead(), 300);
      return () => clearTimeout(t);
    }
  }, [active, unreadCount, onMarkNotifsRead]);

  return (
    <section className="screen pf-screen" data-testid="profile-screen">
      <div className="pf-bg-orb pf-bg-orb-1" />
      <div className="pf-bg-orb pf-bg-orb-2" />

      <div className="pf-topbar" data-testid="profile-topbar">
        <div className="pf-topbar-brand">
          <img src="/assets/logo.png" alt="PULPAY" />
          <span className="pf-topbar-title">Profil</span>
        </div>
      </div>

      <div className="content pf-content">
        <div className="pf-hero" data-testid="profile-hero">
          <div className="pf-hero-glow" />
          <div className="pf-hero-brand">
            <img src="/assets/logo.png" alt="" />
            <span><b>PULPAY</b><i>ÜZVLÜK</i></span>
          </div>
          <div className="pf-hero-row">
            <div className={`pf-avatar pf-avatar-${gender || "none"}`} data-testid="profile-avatar">
              <img src="/assets/logo.png" alt="PULPAY" className="pf-avatar-logo" />
              <span className="pf-avatar-badge">
                <img src="/assets/logo.png" alt="" />
              </span>
            </div>
            <div className="pf-hero-info">
              <h3 data-testid="profile-name">{firstName}</h3>
              <p data-testid="profile-phone">{userPhone || "+994 50 123 45 67"}</p>
              <span className="pf-hero-tag">Təsdiqlənmiş üzv</span>
            </div>
          </div>
        </div>

        <div className="pf-menu" data-testid="profile-menu">
          {MENU.map((m) => {
            const Icon = m.Icon;
            return (
              <button
                key={m.key}
                type="button"
                className={`pf-menu-row pf-tint-${m.tint}`}
                onClick={() => setActive(m.key)}
                data-testid={`profile-menu-${m.key}`}
              >
                <span className="pf-menu-ic"><Icon size={18} strokeWidth={2.4} /></span>
                <span className="pf-menu-label">{m.label}</span>
                <span className="pf-menu-end">
                  <ChevronRight size={18} />
                </span>
              </button>
            );
          })}
        </div>

        <button className="pf-logout" onClick={onLogout} data-testid="profile-logout">
          <LogOut size={18} /> Çıxış
        </button>
      </div>

      {active && (
        <ProfileInfoModal
          item={content[active]}
          activeKey={active}
          onClose={() => setActive(null)}
          notify={notify}
          history={notifsHistory}
          onClearHistory={onClearNotifs}
        />
      )}

      <BottomNav active="profile" go={go} notify={notify} />
    </section>
  );
}
